import React, { useCallback, useContext, useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { useLocation } from "@reach/router";
import classNames from "classnames";
import { Link } from "gatsby";

import { getCookieAccepted, removeCookieAccepted, setCookieAccepted } from "@/account/user";
import { DECLINED_COOKIE_SETTINGS, DEFAULT_COOKIE_SETTINGS } from "@/components/constants";
import { UserContextType } from "@/contexts/User/types";
import UserContext from "@/contexts/User/UserContext";
import { isPageWithoutMobileBottomMenu } from "@/utils/urlUtils";
import { Button, ButtonColorEnum, ButtonSizeEnum } from "@components/common/button";

import { COOKIE_BANNER_SHOW_DELAY_MS } from "./constants";
import {
  saveUserCookieChoice,
  saveUserCookieChoiceVariables,
} from "./graphql/__generated__/saveUserCookieChoice";
import { GET_USER_COOKIE_CHOICE } from "./graphql/GET_USER_COOKIE_CHOICE";
import { SAVE_USER_COOKIE_CHOICE } from "./graphql/SAVE_USER_COOKIE_CHOICE";
import { parseStoredUserCookieChoice } from "./utils";
import "./styles.scss";

const CookieBanner = () => {
  const { setIsCookiePermitted, isUserLoggedIn } = useContext<UserContextType>(UserContext);
  const location = useLocation();
  const hasNoBottomMenu = isPageWithoutMobileBottomMenu(location.pathname);
  const [isBannerPending, setIsBannerPending] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [showBannerImmediately, setShowBannerImmediately] = useState(false);
  const [isSavingCookieChoice, setIsSavingCookieChoice] = useState(false);

  const hideBanner = useCallback(() => {
    setShowBannerImmediately(false);
    setIsBannerPending(false);
  }, []);

  const scheduleBanner = useCallback(() => {
    setIsBannerPending((isPending) => {
      if (isPending) {
        return isPending;
      }

      setShowBannerImmediately(false);
      return true;
    });
  }, []);

  const showBannerNow = useCallback(() => {
    setShowBannerImmediately(true);
    setIsBannerPending(true);
  }, []);

  useEffect(() => {
    if (!isBannerPending) {
      setIsBannerVisible(false);
      return undefined;
    }

    if (showBannerImmediately) {
      setIsBannerVisible(true);
      return undefined;
    }

    setIsBannerVisible(false);
    const timerId = window.setTimeout(
      () =>
        setIsBannerVisible(true),
      COOKIE_BANNER_SHOW_DELAY_MS,
    );

    return () =>
      window.clearTimeout(timerId);
  }, [isBannerPending, location.pathname, showBannerImmediately]);

  const applyServerCookieChoice = useCallback((raw: boolean | null | undefined) => {
    const { hasStoredChoice, permitted } = parseStoredUserCookieChoice(raw);

    if (hasStoredChoice) {
      setCookieAccepted(permitted);
      setIsCookiePermitted(permitted);
      hideBanner();
      return;
    }

    removeCookieAccepted();
    setIsCookiePermitted(true);
    scheduleBanner();
  }, [hideBanner, scheduleBanner, setIsCookiePermitted]);

  const applyLocalChoiceFallback = useCallback(() => {
    // Network or auth glitches should not leave banner in stale state.
    const localChoice = getCookieAccepted();

    if (localChoice === "true" || localChoice === "false") {
      const permitted = localChoice === "true";
      setIsCookiePermitted(permitted);
      hideBanner();
      return;
    }

    removeCookieAccepted();
    setIsCookiePermitted(true);
    scheduleBanner();
  }, [hideBanner, scheduleBanner, setIsCookiePermitted]);

  const { refetch } = useQuery(GET_USER_COOKIE_CHOICE, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      applyServerCookieChoice(response?.getUserCookieChoice?.cookie);
    },
    onError: applyLocalChoiceFallback,
  });

  useEffect(() => {
    refetch()
      .then((response) => {
        applyServerCookieChoice(response?.data?.getUserCookieChoice?.cookie);
      })
      .catch(applyLocalChoiceFallback);
  }, [applyLocalChoiceFallback, applyServerCookieChoice, isUserLoggedIn, refetch]);

  const [saveCookieChoice] = useMutation<
    saveUserCookieChoice,
    saveUserCookieChoiceVariables
  >(SAVE_USER_COOKIE_CHOICE, {
    onError: () => {
      setCookieAccepted(false);
      setIsCookiePermitted(false);
      showBannerNow();
    },
  });

  const onAcceptClick = async () => {
    setCookieAccepted(true);
    setIsCookiePermitted(true);
    setIsSavingCookieChoice(true);

    try {
      await saveCookieChoice({
        variables: {
          input: {
            settings: DEFAULT_COOKIE_SETTINGS,
          },
        },
      });
      hideBanner();
    } catch {
      // `onError` handler в useMutation сам вернет UI в состояние "показывать баннер".
    } finally {
      setIsSavingCookieChoice(false);
    }
  };

  const onDeclineClick = async () => {
    setCookieAccepted(false);
    setIsCookiePermitted(false);
    setIsSavingCookieChoice(true);

    try {
      await saveCookieChoice({
        variables: {
          input: {
            settings: DECLINED_COOKIE_SETTINGS,
          },
        },
      });
      hideBanner();
    } catch {
      // `onError` handler в useMutation сам вернет UI в состояние "показывать баннер".
    } finally {
      setIsSavingCookieChoice(false);
    }
  };

  if (!isBannerVisible) return null;

  return (
    <div
      className={classNames("cookie-banner", {
        "cookie-banner__no-bottom-menu": hasNoBottomMenu,
      })}
    >
      <div className="cookie-banner__content">
        <div className="cookie-banner__info">
          <div className="cookie-banner__copy">
            <div className="cookie-banner__title">Мы используем cookies и Яндекс Метрику</div>
            <div className="cookie-banner__text">
              Обработка данных согласно
              {" "}
              <Link to="/privacy-policy">Политике конфиденциальности</Link>
              .
              {" "}
              Продолжая, вы даёте
              {" "}
              <Link to="/cookie-agree">согласие</Link>
              {" "}
              на обработку персональных данных в соответствии с
              {" "}
              <Link to="/cookie-policy">Политикой обработки cookie</Link>
              .
            </div>
          </div>

          <div className="cookie-banner__actions">
            <Button
              text="Отказаться"
              size={ButtonSizeEnum.XSmall}
              color={ButtonColorEnum.Light}
              onClick={onDeclineClick}
              disabled={isSavingCookieChoice}
              isLoading={isSavingCookieChoice}
              className="cookie-banner__button-decline"
            />
            <Button
              text="Хорошо"
              size={ButtonSizeEnum.XSmall}
              color={ButtonColorEnum.Dark}
              onClick={onAcceptClick}
              disabled={isSavingCookieChoice}
              className="cookie-banner__button-accept"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
